import { ColourPalette } from "@esenda/core-ui/constants";

/**
 * Font families supported by the checkoutTheme URL parameter.
 */
export type CheckoutThemeFontFamily =
    "default" | "sans-serif" | "serif" | "monospace";

/**
 * Version 1 options accepted in the checkoutTheme URL parameter.
 * Every setting except version is optional; omitted settings keep checkout's
 * existing appearance. Provider-controlled surfaces may not support all
 * options. This interface describes the contract.
 */
export interface CheckoutTheme {
    /** Theme schema version; checkout currently accepts only version 1. */
    version: 1;
    /** Primary action colour, as an opaque #RRGGBB value. */
    primaryColor?: string;
    /** Text colour on primary-coloured actions; must meet contrast requirements. */
    primaryTextColor?: string;
    /** General checkout text colour; must contrast with the page and field surfaces. */
    textColor?: string;
    /** Checkout page background colour. */
    backgroundColor?: string;
    /** Form-field background colour. */
    fieldBackgroundColor?: string;
    /** Form-field border colour; must meet the minimum boundary contrast. */
    borderColor?: string;
    /** Predefined local font stack; `default` preserves checkout's current font. */
    fontFamily?: CheckoutThemeFontFamily;
    /** Button corner radius in pixels, from 0 through 16. */
    buttonRadius?: number;
    /** Input corner radius in pixels, from 0 through 16. */
    inputRadius?: number;
    /** Checkout content padding in pixels, from 8 through 24. */
    contentPadding?: number;
}

/**
 * Reasons why the checkoutTheme URL parameter may be rejected.
 */
export type CheckoutThemeRejectionReason =
    | "duplicate-parameter"
    | "payload-too-large"
    | "invalid-json"
    | "invalid-theme"
    | "unsupported-version"
    | "unknown-option"
    | "invalid-option"
    | "insufficient-contrast";

/**
 * Result of parsing the checkoutTheme URL parameter.
 */
export type CheckoutThemeParseResult =
    | { status: "missing"; theme: null }
    | { status: "valid"; theme: Readonly<CheckoutTheme> }
    | {
          status: "invalid";
          theme: null;
          reason: CheckoutThemeRejectionReason;
      };

const MAX_THEME_BYTES = 4096;

/**
 * Allowed option keys for the checkoutTheme URL parameter.
 */
const ALLOWED_OPTIONS = new Set([
    "version",
    "primaryColor",
    "primaryTextColor",
    "textColor",
    "backgroundColor",
    "fieldBackgroundColor",
    "borderColor",
    "fontFamily",
    "buttonRadius",
    "inputRadius",
    "contentPadding",
]);

const FONT_FAMILIES = new Set<CheckoutThemeFontFamily>([
    "default",
    "sans-serif",
    "serif",
    "monospace",
]);

/**
 * Colour-related option keys for the checkoutTheme URL parameter.
 */
const COLOR_OPTIONS = [
    "primaryColor",
    "primaryTextColor",
    "textColor",
    "backgroundColor",
    "fieldBackgroundColor",
    "borderColor",
] as const;

/** Narrows parsed JSON to a plain object, excluding arrays and null. */
const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === "object" && value !== null && !Array.isArray(value);

/** Accepts only opaque six-digit hexadecimal colours from the URL contract. */
const isHexColor = (value: unknown): value is string =>
    typeof value === "string" && /^#[0-9a-fA-F]{6}$/.test(value);

/** Converts a validated hex colour to the WCAG relative-luminance scale. */
const getRelativeLuminance = (color: string): number => {
    const channels = color
        .slice(1)
        .match(/.{2}/g)
        ?.map((channel) => parseInt(channel, 16) / 255);

    if (!channels) {
        return 0;
    }

    const [red, green, blue] = channels.map((channel) =>
        channel <= 0.04045
            ? channel / 12.92
            : ((channel + 0.055) / 1.055) ** 2.4,
    );

    return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
};

/**
 * Picks the wallet (Apple Pay / Google Pay) button style that stands out most
 * against the theme's background. Both providers recommend a black button on
 * light backgrounds and a white button on dark backgrounds.
 *
 * @param backgroundColor Theme background as #rrggbb, or null/undefined for checkout's default.
 * @returns 'black' for light backgrounds, 'white' for dark backgrounds.
 */
export const getWalletPaymentButtonColour = (
    backgroundColor: string | null | undefined,
): "white" | "black" => {
    const background = backgroundColor ?? ColourPalette.WHITE;

    return getContrastRatio("#000000", background) >=
        getContrastRatio("#ffffff", background)
        ? "black"
        : "white";
};

/** Calculates the WCAG contrast ratio for two validated hex colours. */
export const getContrastRatio = (
    foreground: string,
    background: string,
): number => {
    const luminances = [
        getRelativeLuminance(foreground),
        getRelativeLuminance(background),
    ].sort((left, right) => right - left);

    return (luminances[0] + 0.05) / (luminances[1] + 0.05);
};

/** Checks whether two colours meet the caller's minimum contrast ratio. */
const hasSufficientContrast = (
    foreground: string,
    background: string,
    minimumRatio: number,
): boolean => getContrastRatio(foreground, background) >= minimumRatio;

/**
 * Validates supported option values and their contrast against checkout
 * defaults, so partial themes remain accessible when merged with defaults.
 */
const validateOptions = (
    input: Record<string, unknown>,
): CheckoutThemeRejectionReason | null => {
    if (input.version !== 1) {
        return "unsupported-version";
    }

    if (Object.keys(input).some((key) => !ALLOWED_OPTIONS.has(key))) {
        return "unknown-option";
    }

    if (
        COLOR_OPTIONS.some(
            (key) => input[key] !== undefined && !isHexColor(input[key]),
        )
    ) {
        return "invalid-option";
    }

    if (
        input.fontFamily !== undefined &&
        (typeof input.fontFamily !== "string" ||
            !FONT_FAMILIES.has(input.fontFamily as CheckoutThemeFontFamily))
    ) {
        return "invalid-option";
    }

    if (
        (input.buttonRadius !== undefined &&
            (!Number.isInteger(input.buttonRadius) ||
                (input.buttonRadius as number) < 0 ||
                (input.buttonRadius as number) > 16)) ||
        (input.inputRadius !== undefined &&
            (!Number.isInteger(input.inputRadius) ||
                (input.inputRadius as number) < 0 ||
                (input.inputRadius as number) > 16)) ||
        (input.contentPadding !== undefined &&
            (!Number.isInteger(input.contentPadding) ||
                (input.contentPadding as number) < 8 ||
                (input.contentPadding as number) > 24))
    ) {
        return "invalid-option";
    }

    const primaryColor = input.primaryColor ?? ColourPalette.PRIMARY_400;
    const primaryTextColor = input.primaryTextColor ?? ColourPalette.WHITE;
    const textColor = input.textColor ?? ColourPalette.INK;
    const backgroundColor = input.backgroundColor ?? ColourPalette.WHITE;
    const fieldBackgroundColor =
        input.fieldBackgroundColor ?? ColourPalette.WHITE;

    if (
        !hasSufficientContrast(
            primaryTextColor as string,
            primaryColor as string,
            4.5,
        ) ||
        !hasSufficientContrast(
            textColor as string,
            backgroundColor as string,
            4.5,
        ) ||
        !hasSufficientContrast(
            textColor as string,
            fieldBackgroundColor as string,
            4.5,
        )
    ) {
        return "insufficient-contrast";
    }

    if (
        (input.primaryColor !== undefined ||
            input.backgroundColor !== undefined ||
            input.fieldBackgroundColor !== undefined) &&
        (!hasSufficientContrast(
            primaryColor as string,
            backgroundColor as string,
            3,
        ) ||
            !hasSufficientContrast(
                primaryColor as string,
                fieldBackgroundColor as string,
                3,
            ))
    ) {
        return "insufficient-contrast";
    }

    // Only partner-supplied borders are held to 3:1; checkout's default border
    // is a design-system value and is left unchanged when borderColor is omitted.
    if (
        input.borderColor !== undefined &&
        !hasSufficientContrast(
            input.borderColor as string,
            fieldBackgroundColor as string,
            3,
        )
    ) {
        return "insufficient-contrast";
    }

    return null;
};

/**
 * Parses and validates the optional startup theme while preserving a rejection
 * reason for the integration layer. Missing or invalid themes have no value,
 * allowing checkout to retain its existing default styling.
 *
 * @param search URL query string, with or without a leading question mark.
 * @returns A frozen theme, null for a missing theme, or null with a reason on rejection.
 */
export const parseCheckoutTheme = (
    search: string,
): CheckoutThemeParseResult => {
    const values = new URLSearchParams(search).getAll("checkoutTheme");

    if (values.length === 0) {
        return { status: "missing", theme: null };
    }

    if (values.length > 1) {
        return {
            status: "invalid",
            theme: null,
            reason: "duplicate-parameter",
        };
    }

    const [encodedTheme] = values;
    if (new TextEncoder().encode(encodedTheme).byteLength > MAX_THEME_BYTES) {
        return {
            status: "invalid",
            theme: null,
            reason: "payload-too-large",
        };
    }

    let parsedTheme: unknown;
    try {
        parsedTheme = JSON.parse(encodedTheme);
    } catch {
        return {
            status: "invalid",
            theme: null,
            reason: "invalid-json",
        };
    }

    if (!isRecord(parsedTheme)) {
        return {
            status: "invalid",
            theme: null,
            reason: "invalid-theme",
        };
    }

    const rejectionReason = validateOptions(parsedTheme);
    if (rejectionReason) {
        return {
            status: "invalid",
            theme: null,
            reason: rejectionReason,
        };
    }

    const theme = Object.freeze({
        ...parsedTheme,
        ...Object.fromEntries(
            COLOR_OPTIONS.filter((key) => parsedTheme[key] !== undefined).map(
                (key) => [key, (parsedTheme[key] as string).toLowerCase()],
            ),
        ),
    }) as Readonly<CheckoutTheme>;

    return { status: "valid", theme };
};

/**
 * Reads a theme without side effects; null means checkout should use defaults.
 * Use `parseCheckoutTheme` when the caller also needs a rejection reason.
 *
 * @param search URL query string, with or without a leading question mark.
 * @returns The frozen validated theme, or null when it is absent or invalid.
 */
export const readCheckoutTheme = (
    search: string,
): Readonly<CheckoutTheme> | null => parseCheckoutTheme(search).theme;

/** Logs one warning for an invalid parse result; missing optional themes are silent. */
export const warnInvalidCheckoutTheme = (
    result: CheckoutThemeParseResult,
): void => {
    if (result.status === "invalid") {
        console.warn(
            "Ignoring invalid checkoutTheme parameter:",
            result.reason,
        );
    }
};
